import DOMPurify from 'dompurify';
import { marked, Renderer, Tokens } from 'marked';
import type { KnowledgebaseCategory } from '@awo-care/shared-core';

/**
 * Rendert einen KB-Artikel als HTML, optional mit hervorgehobener Fundstelle.
 * `marked` filtert nicht — DOMPurify läuft als letzter Schritt, damit das
 * Ergebnis sicher am Angular-Sanitizer vorbei ins DOM darf (#804).
 */
export function renderKbArticleHtml(
  content: string,
  category: KnowledgebaseCategory,
  highlightText?: string
): string {
  let html = marked.parse(content, {
    breaks: true,
    gfm: true,
    renderer: createKbRenderer(category),
  }) as string;

  if (highlightText) {
    html = highlightInHtml(html, highlightText);
  }

  return DOMPurify.sanitize(html, { ADD_ATTR: ['target'] });
}

/**
 * Highlights text in rendered HTML without breaking tags.
 * Uses a fuzzy approach: splits the search text into sentences/phrases
 * and highlights each match found in text nodes only.
 */
function highlightInHtml(html: string, searchText: string): string {
  // Split into meaningful phrases (by sentence boundaries or line breaks)
  const phrases = searchText
    .split(/[.\n]+/)
    .map(p => p.trim())
    .filter(p => p.length > 15);

  // If no long phrases, try the whole text
  if (phrases.length === 0 && searchText.trim().length > 5) {
    phrases.push(searchText.trim());
  }

  let result = html;
  for (const phrase of phrases) {
    const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Only match in text content (not inside HTML tags)
    // Lookahead/lookbehind don't consume > and <, so don't add them in replacement
    result = result.replace(
      new RegExp(`(?<=>)([^<]*?)(${escaped})([^<]*?)(?=<)`, 'gi'),
      (_, before, match, after) =>
        `${before}<span class="grounding-highlight">${match}</span>${after}`
    );
  }
  return result;
}

function createKbRenderer(currentCategory: KnowledgebaseCategory): Renderer {
  const renderer = new Renderer();
  renderer.link = ({ href, text }: Tokens.Link) => {
    if (href?.endsWith('.md') && !href.startsWith('http')) {
      const match = href.match(
        /(?:\.\.\/)?(?:(customer|employee|admin)\/)?(.+)\.md$/
      );
      if (match) {
        const cat = match[1] || currentCategory;
        const slug = match[2];
        return `<a href="/knowledgebase/${cat}-${slug}">${text}</a>`;
      }
    }
    return `<a href="${href}" target="_blank" rel="noopener">${text}</a>`;
  };
  return renderer;
}
